import { useState, useCallback } from 'react';

export type ToastType = 'default' | 'destructive' | 'success' | 'warning' | 'info';

export interface Toast {
  id: string;
  title?: string;
  description?: string;
  action?: React.ReactNode;
  variant?: ToastType;
  duration?: number;
}

const DEFAULT_TOAST_DURATION = 5000; // 5 seconds

export interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback(({ title, description, variant = 'default', action, duration = DEFAULT_TOAST_DURATION }: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast: Toast = {
      id,
      title,
      description,
      variant,
      action,
      duration,
    };

    setToasts((prevToasts) => [...prevToasts, newToast]);

    // Auto-remove toast after duration
    if (duration !== Infinity) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  // Simplified toast method for common uses
  const toast = (props: Omit<Toast, 'id'>) => addToast(props);

  // Convenience methods
  toast.success = (props: Omit<Omit<Toast, 'id'>, 'variant'>) => 
    addToast({ ...props, variant: 'success' });
  
  toast.error = (props: Omit<Omit<Toast, 'id'>, 'variant'>) => 
    addToast({ ...props, variant: 'destructive' });
  
  toast.warning = (props: Omit<Omit<Toast, 'id'>, 'variant'>) => 
    addToast({ ...props, variant: 'warning' });
  
  toast.info = (props: Omit<Omit<Toast, 'id'>, 'variant'>) => 
    addToast({ ...props, variant: 'info' });

  return {
    toast,
    toasts,
    addToast,
    removeToast,
  };
} 